import React from "react";
import { FaInstagram } from "react-icons/fa"
export default function Top10Mistakes() {
  return (
    <section className="relative bg-[#02042b] text-white mt-12">
      {/* subtle glow accents */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(900px 400px at 10% -10%, rgba(99,102,241,.18), transparent 60%), radial-gradient(800px 360px at 120% 0%, rgba(236,72,153,.15), transparent 55%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Hero */}
        <header className="text-center max-w-3xl mx-auto mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            Top 10 Mistakes to Avoid in Assignment Writing (With Real Examples)
          </h1>
          <p className="mt-3 text-gray-300">
            Meta description- Top 10 Mistakes to Avoid in Assignment Writing: real examples, quick fixes, and a proofreading checklist to boost grades, staying plagiarism-free.
          </p>
        
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* TOC (sticky on desktop) */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 bg-white/5 backdrop-blur rounded-xl border border-white/10 p-5">
              <h2 className="text-lg font-semibold mb-3">Table of Contents</h2>
              <nav className="text-sm text-gray-300 space-y-2">
                <a href="#intro" className="block hover:text-white">Introduction</a>
                <a href="#keypoints" className="block hover:text-white">Key Points</a>
                <a href="#mistakes" className="block hover:text-white">Top 10 Mistakes</a>
                <a href="#examples" className="block hover:text-white">Real Examples</a>
                <a href="#timemanagement" className="block hover:text-white">Time Management (7-Day)</a>
                <a href="#introconclusion" className="block hover:text-white">Build Intros & Conclusions</a>
                <a href="#grammar" className="block hover:text-white">Grammar & Punctuation</a>
                <a href="#fallacies" className="block hover:text-white">Logical Fallacies</a>
                <a href="#styleguides" className="block hover:text-white">Style Guides</a>
                <a href="#ai" className="block hover:text-white">Responsible Use of AI</a>
                <a href="#checklist" className="block hover:text-white">Quick Checklist</a>
                <a href="#support" className="block hover:text-white">Our Support</a>
                <a href="#faq" className="block hover:text-white">FAQ</a>
                <a href="#conclusion" className="block hover:text-white">Conclusion</a>
                 <a href="#acknowledgement" className="block hover:text-white">Acknowledgement</a>
              </nav>
            </div>
          </aside>

          {/* Article */}
          <article className="lg:col-span-8 space-y-10">
            {/* Introduction */}
            <section id="intro" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Introduction</h2>
              <p className="text-gray-200">
                To avoid losing easy marks, you must sidestep the Top 10 Mistakes to Avoid in Assignment Writing. Start by decoding the assignment brief and marking rubric so you know exactly what’s being assessed.
              </p>
              <p className="text-gray-200 mt-3">
                Lock in a focused thesis statement and research question, then map an outline with clear topic sentences for cohesion and coherence. Build your case on peer-reviewed evidence (via Google Scholar and library databases) and cite consistently in APA or Harvard.
              </p>
              <p className="text-gray-200 mt-3">
                Finally, run a tight proofreading pass to fix grammar and punctuation, align word count, and prevent accidental plagiarism in your Turnitin report. Follow these steps and your ideas will read sharper, score higher, and feel far less stressful to submit.
              </p>
            </section>

            {/* Key Points */}
            <section id="keypoints" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-4">Key Points</h2>
              <ul className="list-disc pl-6 space-y-3 text-gray-200">
                <li>Read the brief & rubric: extract verbs, criteria, formatting rules, and word count.</li>
                <li>Write a clear thesis & question: your thesis statement must directly answer the task.</li>
                <li>Plan the structure: outline sections; use topic sentences, signposting, cohesion and coherence.</li>
                <li>Use credible sources & cite: rely on peer-reviewed research; keep APA/MLA/Harvard consistent; complete the reference list.</li>
                <li>Proofread before submission: fix grammar/punctuation, check plagiarism/Turnitin, and confirm formatting compliance.</li>
              </ul>
            </section>

            {/* Top 10 Mistakes */}
            <section id="mistakes" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-4">Top 10 Mistakes to Avoid in Assignment Writing</h2>

              <div className="space-y-4 text-gray-200">
                <div>
                  <h3 className="font-semibold text-white">1) Skimming the Brief and Rubric</h3>
                  <p className="mt-2">Mistake: Reading the title, not the details, like assessment criteria, required referencing style, or word count.</p>
                  <p className="mt-1">Why it hurts: Markers grade against the rubric and learning outcomes.</p>
                  <p className="mt-1">Fix: Annotate command verbs (evaluate, compare, synthesize). Map each criterion to a paragraph. Confirm formatting: headings, line spacing, reference list, and appendices.</p>
                </div>

                <div>
                  <h3 className="font-semibold text-white">2) Missing or Weak Thesis Statement and Research Question</h3>
                  <p className="mt-2">Mistake: Writing without a guiding thesis or question.</p>
                  <p className="mt-1">Fix: Draft a one-sentence thesis with stance + “because.” For exploratory tasks, pose a focused research question and refine it after reading.</p>
                </div>

                <div>
                  <h3 className="font-semibold text-white">3) Writing Without an Outline (Coherence &gt; Chaos)</h3>
                  <p className="mt-2">Mistake: “I’ll figure it out as I go.”</p>
                  <p className="mt-1">Fix: Plan headings; write topic sentences that directly support the thesis; use signposting (however, therefore, in contrast) for cohesion and coherence.</p>
                  <p className="mt-1">Word-count guide (1,500 words): Intro 10% • Context/Lit 25% • Analysis 45% • Counter-argument 10% • Conclusion 10%.</p>
                </div>

                <div>
                  <h3 className="font-semibold text-white">4) Superficial Research & Weak Sources</h3>
                  <p className="mt-2">Mistake: Citing blogs or old lecture slides.</p>
                  <p className="mt-1">Fix: Prioritise peer-reviewed journals, systematic reviews, and meta-analyses. Track DOIs and dates; balance primary studies with secondary sources.</p>
                </div>

                <div>
                  <h3 className="font-semibold text-white">5) Patchwriting, Plagiarism, and Poor Paraphrasing</h3>
                  <p className="mt-2">Mistake: Rewording line-by-line or forgetting to cite.</p>
                  <p className="mt-1">3-step paraphrase routine:</p>
                  <ol className="list-decimal pl-6 space-y-1 mt-2 text-gray-200">
                    <li>Read and close the source.</li>
                    <li>Rewrite from memory, changing structure and emphasis.</li>
                    <li>Check accuracy; add in-text citation.</li>
                  </ol>
                  <p className="mt-2">Tip: Understand common knowledge vs. unique claims. Use a plagiarism checker judiciously.</p>
                </div>

                <div>
                  <h3 className="font-semibold text-white">6) Incorrect Referencing and In-Text Citations</h3>
                  <p className="mt-2">Mistake: Mixing styles or missing key details.</p>
                  <p className="mt-1">Fix: Choose one style (APA 7th, MLA 9th, Harvard, Chicago 17th). Use Zotero/Mendeley and double-check bibliography formatting. Every in-text entry must match the reference list.</p>
                </div>

                <div>
                  <h3 className="font-semibold text-white">7) Being Descriptive Instead of Critical Analysis</h3>
                  <p className="mt-2">Mistake: “Author A says X. Author B says Y.”</p>
                  <p className="mt-1">Fix: Evaluate methodology (e.g., qualitative, quantitative, mixed methods), sample size, and potential confounding variables; explain contradictions; tie back to your thesis.</p>
                </div>

                <div>
                  <h3 className="font-semibold text-white">8) Misusing Data, Graphs, and Evidence</h3>
                  <p className="mt-2">Mistake: Dropping statistics without context.</p>
                  <p className="mt-1">Fix: Introduce each figure, add interpretation, and state limits. Use clear data visualization, label units, and include a concise figure legend.</p>
                </div>

                <div>
                  <h3 className="font-semibold text-white">9) Style & Tone: Over-Informal, Over-Jargon, or Over-Passive</h3>
                  <p className="mt-2">Mistake: Slang-heavy or overly passive writing.</p>
                  <p className="mt-1">Fix: Prefer active voice, keep necessary hedging (“may,” “suggests”), define terms on first use, and read aloud to check readability.</p>
                </div>

                <div>
                  <h3 className="font-semibold text-white">10) Skipping Editing, Proofreading, and Final Checks</h3>
                  <p className="mt-2">Mistake: Submitting the first draft.</p>
                  <p className="mt-1">30-minute final pass: Structure → Flow → Style → References → Format (title page, abstract if required, table of contents, appendix). Tools can help (Grammarly, Hemingway Editor, text-to-speech), but you make the final call.</p>
                </div>
              </div>
            </section>

            {/* Real Examples */}
            <section id="examples" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Real Examples of the Top 10 Mistakes to Avoid in Assignment Writing</h2>
              <div className="text-gray-200 space-y-3">
                <p><strong>Brief vs. Output:</strong> Brief says “critically evaluate”; student writes a historical overview. Fix: Insert evaluation sentences (limitations, alternative explanations) in each subsection.</p>
                <p><strong>Weak thesis:</strong> “This essay discusses social media.” Fix: “This essay argues that algorithmic curation reduces adolescent attention spans; however, media-literacy instruction mitigates this effect.”</p>
                <p><strong>Data misuse:</strong> “Productivity +25%” graphic with no sample detail. Fix: Add n, duration, and controls; relate to your research question.</p>
                <p><strong>Paraphrasing fails:</strong> Structure mirrors the original. Fix: Change order/emphasis and cite.</p>
                <p><strong>Referencing mismatch:</strong> In-text sources missing from reference list. Fix: One-to-one reconciliation with your citation manager.</p>
              </div>
            </section>

            {/* Time Management snippet included as requested */}
            <section id="timemanagement" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Time Management for Assignments: A 7-Day Plan</h2>
              <p className="text-gray-200">Goal: Avoid last-minute stress and improve depth of thinking.</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-200 mt-3">
                <li><strong>Day 1</strong> – Decode the task: Annotate the assignment brief and marking rubric; extract verbs and learning outcomes. Create your working thesis and research question.</li>
                <li><strong>Day 2</strong> – Find sources: Search Google Scholar and library databases. Aim for 6–12 peer-reviewed sources; log DOIs.</li>
                <li><strong>Day 3</strong> – Outline: Headings, topic sentences, and word-count allocation. Decide what goes to appendices (raw tables, instruments).</li>
                <li><strong>Day 4</strong> – Draft 1 (analysis first): Write core argument sections while evidence is fresh.</li>
                <li><strong>Day 5</strong> – Draft 2 (intro/conclusion): Build a strong opening and synthesis-style conclusion.</li>
                <li><strong>Day 6</strong> – Style & references: Tighten sentences, convert passive to active, standardize in-text citations and reference list.</li>
                <li><strong>Day 7</strong> – Proofread & submit: Run the cheat sheet below; read aloud; final Turnitin check if allowed.</li>
              </ul>
              <p className="text-gray-200 mt-3"><strong>Micro-habit:</strong> Use the Pomodoro technique (25/5) and schedule two “buffer” sessions.</p>
            </section>

            {/* Intros & Conclusions */}
            <section id="introconclusion" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Build Introductions & Conclusions Fast</h2>
              <div className="text-gray-200 space-y-3">
                <p><strong>Intro pattern (3 moves):</strong></p>
                <ol className="list-decimal pl-6">
                  <li>Context: Name the debate or problem space.</li>
                  <li>Narrowing: Identify the key gap, controversy, or criterion.</li>
                  <li>Thesis: State your position + the “because.”</li>
                </ol>

                <p className="mt-3"><strong>Conclusion pattern (3 moves):</strong></p>
                <ol className="list-decimal pl-6">
                  <li>Synthesis: Reassemble your main claims (no new evidence).</li>
                  <li>Implications: Why your findings matter for practice/policy/theory.</li>
                  <li>Next step: A limitation to address or a future research direction.</li>
                </ol>
              </div>
            </section>

            {/* Grammar & Punctuation */}
            <section id="grammar" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Grammar & Punctuation Pitfalls (Cheat Sheet)</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Fragments: Ensure every sentence has a subject + finite verb.</li>
                <li>Run-ons/Comma splices: Use a period or semicolon to join independent clauses.</li>
                <li>Subject–verb agreement: Singular subjects take singular verbs.</li>
                <li>Pronoun reference: Make the antecedent crystal clear.</li>
                <li>Parallelism: Keep listed items in the same grammatical form.</li>
                <li>Apostrophes: Possession (student’s / students’), not plurals.</li>
                <li>Colons vs. semicolons: Colon introduces; semicolon joins related sentences.</li>
                <li>Quotation marks: In most styles, commas/periods stay inside.</li>
                <li>Hyphenation: Compound modifiers before nouns (e.g., evidence-based practice).</li>
                <li>Numbers: Follow your style guide (e.g., APA words for one–nine).</li>
              </ul>
            </section>

            {/* Logical fallacies */}
            <section id="fallacies" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Avoid Logical Fallacies & Strengthen Critical Analysis</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Hasty generalization: Small or biased sample size → sweeping claim.</li>
                <li>Straw man: Refuting a weaker version of an opposing argument.</li>
                <li>Ad hominem: Attacking the author, not the argument.</li>
                <li>Appeal to authority: Citation ≠ proof; evaluate methodology.</li>
                <li>False cause (post hoc): Correlation ≠ causation; check confounding variables.</li>
                <li>False dilemma: Presenting two options when more exist.</li>
              </ul>
              <p className="text-gray-200 mt-3">Argument health check: Clear claim • Relevant evidence • Warrants made explicit • Fallacies removed • Tied back to the thesis.</p>
            </section>

            {/* Style guides */}
            <section id="styleguides" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Style Guides at a Glance: APA, MLA, Harvard, Chicago</h2>
              <div className="text-gray-200 space-y-3">
                <p><strong>APA 7th (journal article):</strong></p>
                <p>In-text: (Smith, 2023, p. 45).</p>
                <p>Reference: Smith, J. (2023). Title. Journal Name, 12(3), 123–140. https://doi.org/xx.xxx/xxxxx</p>

                <p className="mt-3"><strong>MLA 9th:</strong></p>
                <p>In-text: (Smith 45).</p>
                <p>Works Cited: Smith, Jane. “Title.” Journal Name, vol. 12, no. 3, 2023, pp. 123–40.</p>

                <p className="mt-3"><strong>Harvard:</strong></p>
                <p>In-text: (Smith 2023, p. 45).</p>
                <p>Reference list: Smith, J 2023, ‘Title’, Journal Name, vol. 12, no. 3, pp. 123–140.</p>

                <p className="mt-3"><strong>Chicago 17th (Notes & Bibliography):</strong></p>
                <p>Footnote: 1. Jane Smith, “Title,” Journal Name 12, no. 3 (2023): 123–40.</p>
                <p>Bibliography: Smith, Jane. “Title.” Journal Name 12, no. 3 (2023): 123–40.</p>
              </div>
            </section>

            {/* AI */}
            <section id="ai" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Responsible Use of Generative AI (Policy-First)</h2>
              <p className="text-gray-200">Reality check: Generative AI can brainstorm, outline, and suggest counter-arguments, but it may fabricate sources and your course may limit its use.</p>
              <h3 className="text-lg font-semibold text-white mt-3">Use responsibly:</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-200 mt-2">
                <li>Brainstorm keywords, produce an outline, or build a revision checklist.</li>
                <li>Verify claims in peer-reviewed literature; write in your own voice.</li>
                <li>Disclose AI assistance if your module requires it.</li>
                <li>Treat AI writing detection as imperfect and follow your academic integrity policy first.</li>
              </ul>
            </section>

            {/* Checklist */}
            <section id="checklist" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Quick Checklist</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Clear thesis statement and aligned research question</li>
                <li>Structured outline with purposeful topic sentences</li>
                <li>Balanced, recent peer-reviewed sources; log DOIs</li>
                <li>Ethical paraphrasing, accurate in-text citations, complete reference list</li>
                <li>Consistent style (APA/MLA/Harvard/Chicago)</li>
                <li>Analytical voice (evaluate methodology, sample size, limitations)</li>
                <li>Clear data visualization with figure legends and context</li>
                <li>Professional tone (active voice, minimal jargon)</li>
                <li>Full proofreading using the cheat sheet</li>
                <li>Policy-compliant, declared (if needed) generative AI support</li>
              </ul>
            </section>

            {/* Support */}
            <section id="support" style={{ scrollMarginTop: "7rem" }} className="bg-gradient-to-r from-rose-500/20 to-indigo-500/20 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-2">Our support</h2>
              <p className="text-gray-200">
                If you’d like expert support applying these steps, Infinite Solution pairs you with subject specialists who can help decode your assignment brief and marking rubric, refine your thesis statement and research question, and build a clear outline with strong topic sentences, cohesion, and coherence.
              </p>
              <p className="text-gray-200 mt-3">
                We guide literature review searches via Google Scholar and library databases, ensure ethical paraphrasing, and format in-text citations and the reference list in APA, MLA, Harvard, or Chicago. Need methods help?
              </p>
              <p className="text-gray-200 mt-3">
                We clarify qualitative/quantitative methodology, basic data analysis, and figure/appendix prep. Before submission, our editors run thorough proofreading, punctuation checks, and policy-compliant plagiarism/Turnitin reviews so your work stays original and properly referenced.
              </p>
              <p className="text-gray-200 mt-3">
                With fast turnarounds, transparent feedback, and 24/7 support for students across the UK, Europe, Australia, and the USA, we make high-quality, academically sound assignment writing easier and less stressful.
              </p>
            </section>

            {/* FAQ */}
            <section id="faq" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">FAQ</h2>
              <div className="text-gray-200 space-y-3">
                <p><strong>How many sources do I need for 1,500 words?</strong> Aim for 8–15 peer-reviewed sources, with at least two systematic reviews or a meta-analysis if available.</p>
                <p><strong>What’s a good thesis formula?</strong> Claim + because + two reasons you will prove. Example: “Hybrid learning improves first-year biology achievement because it increases retrieval practice and reduces travel time.”</p>
                <p><strong>How do I paraphrase without plagiarising?</strong> Use the 3-step routine above and always add an in-text citation. Avoid mirroring structure or unique phrasing.</p>
                <p><strong>My Turnitin score is high, am I doomed?</strong> Not necessarily. References and common phrases can inflate percentages. Tutors often review the similarity report for intent and pattern, not just the number.</p>
                <p><strong>Which referencing style should I choose?</strong> Follow your brief. If unspecified, ask your tutor. Then stick to it, mixing styles costs easy marks.</p>
                <p><strong>What if I’m over the word count?</strong> Cut throat-clearing intros, duplicated evidence, and overly long quotations. Prioritize analysis over description.</p>
              </div>
            </section>

            {/* Conclusion */}
            <section id="conclusion" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Conclusion</h2>
              <p className="text-gray-200">
                Top grades rarely come from last-minute dazzling prose. They come from a steady process: read the assignment briefly, build a sharp thesis, plan with an outline, use credible evidence, and finish with precise referencing and thorough proofreading.
              </p>
              <p className="text-gray-200 mt-3">
                Avoiding the Top 10 Mistakes to Avoid in Assignment Writing will make your work clearer, more persuasive, and far less stressful to submit.
              </p>
              <p className="text-gray-200 mt-3">
                Try this now: Pick your next task, apply the 7-day plan, replace two weak sources with peer-reviewed journals, and add one evaluative sentence to every paragraph.
              </p>
            </section>

            <section
              id="acknowledgement"
              style={{ scrollMarginTop: "7rem" }}
              className="bg-gradient-to-r from-indigo-900/40 via-purple-900/30 to-pink-900/20 rounded-xl border border-white/10 p-6 mt-10 shadow-lg"
            >
              <h2 className="text-2xl sm:text-3xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-indigo-300 pb-2">
                Acknowledgement
              </h2>
            
              <p className="text-gray-200">
                The concept was first introduced by{" "}
                <span className="font-semibold text-white">Joy Barua</span>, who set the foundation for the content’s direction.
                <br />
                <a
                  href="https://www.instagram.com/j.b_12111990?igsh=ejBoa2dqbmI1OG4z"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 text-pink-400 hover:text-pink-300 font-semibold"
                >
                  <FaInstagram /> @joybarua
                </a>
              </p>
            
              <p className="text-gray-200 mt-4">
                To shape the initial draft, we utilized advanced AI tools like <span className="font-semibold">ChatGPT</span> and{" "}
                <span className="font-semibold">Google Gemini</span>, ensuring speed, structure, and creativity. Once the draft was
                developed, our team at{" "}
                <a
                  href="https://www.infinitesolution.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 font-semibold hover:text-blue-300 underline"
                >
                  Infinite Solution
                </a>{" "}
                took over to polish it, optimizing for SEO, refining the tone for clarity, and aligning the message with the target
                audience.
              </p>
            
              <p className="text-gray-200 mt-4">
                This collaborative approach seamlessly blended human expertise with AI-driven efficiency, resulting in content that
                is impactful, high-performing, and highly relevant to readers.
              </p>
            </section>
          </article>
        </div>
      </div>
    </section>
  );
}
